import { useEffect, useState, type ReactNode } from "react";
import type { ActiveGame } from "../api/client";
import { clearOnlineGame, loadOnlineGame, type SavedOnlineGame } from "../game/online-resume";
import { BACKGAMMON_SHORT_MATCH_LENGTH, DEFAULT_BACKGAMMON_MATCH_LENGTH } from "@bing/shared";
import {
  getLobbyCategory,
  getResolvedTheme,
  onThemeChange,
  setBackgammonPublicMatchTo,
  setHokmPublicAdvanced,
  setLobbyCategory,
  setPasurPublicSeats,
  setLaddersPublicSeats,
  setThemePref,
  type ResolvedTheme,
} from "../settings/prefs";
import { GAME_ART, GAME_ART_WEBP, SNAKE_ART, SUDOKU_ART } from "../assets/game-art";
import logoUrl from "../assets/logo.jpg";
import { ArtPicture } from "../components/ArtPicture";
import { Avatar } from "../components/Avatar";
import { setResumeSolo } from "../game/fresh-solo";
import { prefetchLaddersScene } from "../game/ladders/prefetch";
import { GameIcon } from "../components/GameIcon";
import { GameMark } from "../components/GameMark";
import { profileDisplayName } from "../components/profile-ui";
import { PromoSlot } from "../components/PromoSlot";
import { Sheet } from "../components/social-ui";
import {
  GAME_CATEGORY,
  GAME_CATEGORY_ORDER,
  isHiddenGame,
  SNAKE_PRESENCE_GAME,
  SUDOKU_PRESENCE_GAME,
  type GameCategory,
  type GameId,
} from "@bing/shared";
import { GAMES, type GameInfo } from "../config";
import { faDigits } from "../i18n/digits";
import { fa } from "../i18n/fa";
import { playSfx } from "../sound/sfx";
import { useSession } from "../store/context";
import type { View } from "../store/session";

/**
 * Screen-local Persian strings for the ported Stitch "Brand Hero v4" lobby that
 * the shared `fa` dictionary doesn't cover yet (this redesign only touches
 * `Lobby`, so — like `Profile`/`Friends` — we don't edit the central i18n file).
 * Fold these into `fa.lobby` when the dictionary grows.
 */
const t = {
  brand: "بینگ گیمز",
  tagline: "«دورِ هم، هر جا که باشی»",
  menu: "منو",
  // Lobby category tabs (card vs board games).
  cardGames: "بازی‌های کارتی",
  boardGames: "بازی‌های تخته‌ای",
  // Nav-drawer header + items (see the drawer note in `Lobby` re: missing backends).
  guestSubtitle: "به بینگ گیمز خوش اومدی",
  drawerStore: "فروشگاه",
  drawerSettings: "تنظیمات",
  drawerHelp: "راهنما",
  drawerHistory: "تاریخچه",
  drawerMessages: "پیام‌ها",
  drawerClose: "بستن",
  /** The top-bar theme toggle's accessible name. */
  themeToggle: "تغییر پوسته",
  // The three-way game chooser (تک‌نفره / public / private) — the app's real start flow.
  // Friendly-casual register (خودمونی): informal 2nd-person, no «شما»/«کنید».
  chooserPrompt: "چطوری می‌خوای بازی کنی؟",
  soloTitle: "تک‌نفره",
  soloHint: "همین حالا با ربات‌ها بازی کن؛ منتظر کسی نمی‌مونی",
  quickTitle: "اتاق عمومی",
  quickHint: "با بازیکن‌های تصادفی هم‌میز شو؛ بازی خودش شروع می‌شه",
  quickTag: "بدون میزبان",
  privateTitle: "با دوستام",
  privateHint: "میزبان خودتی؛ دوستات رو دعوت کن و هر وقت آماده بودی شروع کن",
  privateTag: "میزبان: خودت",
  // حکم ONLY — the second public room. Its own queue, so the hint has to say what is DIFFERENT
  // about the table you land in, not just that it is «advanced».
  advancedTitle: "اتاق عمومی پیشرفته",
  // ⚠️ «سرس / نرس» IS THE SHORTHAND THE INVITE CAPTION USES (`describeTableShape`), and the two
  // must not disagree — a player reads this blurb before joining and the caption after being
  // invited, about the same room. آس نرس is a نرس variant, so the pair carries the same meaning.
  // The full three-way list still lives where it is the POINT: the settings label
  // (`fa.hokm.modes`), its explanatory hint (which DEFINES each one), and the trump picker.
  advancedHint: "حاکم می‌تونه سرس / نرس هم بخونه؛ این حکم‌ها کوت ندارن",
  // تخته نرد ONLY — the second public room. Here the two rooms differ ONLY in match length, so the
  // LENGTH is what the tag has to carry: on this one game «بدون میزبان» is true of both public
  // buttons and therefore says nothing about the choice being made. Built from the same string the
  // host's «طول مسابقه» picker uses, so the two surfaces cannot word the same number differently.
  bgShortTitle: "اتاق عمومی کوتاه",
  bgShortHint: "مسابقهٔ کوتاه‌تر — زودتر تموم می‌شه",
  bgShortTag: fa.backgammon.matchLengthOption(faDigits(BACKGAMMON_SHORT_MATCH_LENGTH)),
  bgStandardTag: fa.backgammon.matchLengthOption(faDigits(DEFAULT_BACKGAMMON_MATCH_LENGTH)),
  // پاسور ONLY — the second public room, the table for two (operator, 2026-10-20). The rooms differ
  // only in SIZE, so — exactly as تخته نرد's lengths — each tag carries its size, built from the same
  // string the host's «بازی چند نفره؟» picker uses.
  pasurTwoTitle: "اتاق عمومی ۲ نفره",
  pasurTwoHint: "رو در رو با یه حریف، بدون یار",
  pasurTwoTag: fa.pasur.seatsOption(faDigits(2)),
  pasurFourTag: fa.pasur.seatsOption(faDigits(4)),
  // مار پله ONLY (card-86) — the same pair as پاسور's: a public table for FOUR and one for TWO, each
  // its own room (a size is a room type, so it is a QUEUE), each tag built from the size picker's
  // own string. No «بدون یار»: مار پله has no partners at either size.
  laddersTwoTitle: "اتاق عمومی ۲ نفره",
  laddersTwoHint: "رو در رو با یه حریف",
  laddersTwoTag: fa.ladders.playersOption(faDigits(2)),
  laddersFourTag: fa.ladders.playersOption(faDigits(4)),
  // NO TAG. «بدون میزبان» is true of it, but «اتاق عمومی» above already carries that chip and a
  // second identical one distinguishes nothing — the title says «پیشرفته» and the hint says what
  // that means. An empty chip is worse than none (the same reason تک‌نفره has none).
} as const;

/** Bundled game photos for the tiles (do NOT use the design's external URLs —
 * they break on filtered networks + CSP). Shared with the invite banners via `GAME_ART`. */
const PHOTOS: Readonly<Record<string, string | undefined>> = {
  ...GAME_ART,
  snake: SNAKE_ART,
  sudoku: SUDOKU_ART,
};

/**
 * «مار» (Snake) — the board tab's LAST tile (operator, 2026-11-02). Played wholly on the phone, so it
 * is not in `GAMES` (the multi-player catalogue every table, stat and leaderboard reads) and `GET
 * /games` has no row for it: the tile is its own entry, always playable, and where the other tiles
 * show a table's player count it says «تک‌نفره». Its live count — how many have its screen open —
 * shows like every other tile's, from presence's `snake` key (none from a game-server that does not
 * count it). It opens Snake's own start screen — no solo/online chooser, no leaderboard, no invite.
 */
const SNAKE_TILE: GameInfo = {
  id: SNAKE_PRESENCE_GAME,
  fa: fa.snake.title,
  en: "Snake",
  players: fa.snake.solo,
  icon: "🐍",
};

/**
 * «سودوکو» — the board tab's last tile, right after «مار» (the operator, 2026-11-03), and built the same
 * way: played on the phone, not in `GAMES`, no `GET /games` row, always playable, «تک‌نفره» where the
 * other tiles show a table's player count, and its live count from presence's `sudoku` key (none from a
 * game-server that does not count it — exactly what «مار»'s tile showed before its count shipped). It
 * opens Sudoku's own start screen — no chooser, no leaderboard, no invite.
 */
const SUDOKU_TILE: GameInfo = {
  id: SUDOKU_PRESENCE_GAME,
  fa: fa.sudoku.title,
  en: "Sudoku",
  players: fa.sudoku.solo,
  icon: "🔢",
};

/** Persian name + emoji per game id, from the lobby catalogue (for the resume strip). */
const GAME_META: Readonly<Record<string, { fa: string; icon: string }>> = Object.fromEntries(
  GAMES.map((g) => [g.id, { fa: g.fa, icon: g.icon }]),
);

/** Short Persian label for an in-progress game's mode (falls back to the raw mode). */
const MODE_FA: Readonly<Record<string, string>> = {
  online: "آنلاین",
  solo: "تک‌نفره",
  quick: "اتاق عمومی",
  private: "میز خصوصی",
};

/** A one-line summary for a resumable game: a server-sent string when present, else the mode. */
function summaryText(g: ActiveGame): string {
  if (typeof g.summary === "string" && g.summary.trim()) return g.summary.trim();
  return MODE_FA[g.mode] ?? g.mode;
}

/**
 * The "continue playing" strip: a horizontal, swipe-able (کشویی) row of the user's
 * resumable in-progress games (from `GET /me/active-games`). Each is a SIMPLE themed
 * card built from the lobby's own design tokens — a felt/gold gradient (felt-deep →
 * felt-edge) carrying the game name, its glyph, a one-line status and the «ادامه»
 * badge — NOT the game's card-back art or photo (which read poorly here). The WHOLE
 * card is the tap-target that resumes that game (the server restores the saved state).
 * The row scroll-snaps and hides its scrollbar. Renders nothing when there are none
 * (a tidy empty state — no orphan heading). Per the exit model, a game only appears
 * here while it is genuinely resumable (kept on «خروج»; removed on finish / «پایان بازی»).
 */
/**
 * ITEM 1 — a short label for the settings a saved game was started with, so two saves of the
 * SAME game read differently on the strip. Empty when the game had nothing to choose (or the
 * row predates settings being recorded), in which case the strip shows only its status.
 */
function savedSettingsLabel(g: ActiveGame): string {
  const parts: string[] = [];
  const d = g.settings?.difficulty;
  if (g.game === "mensch") {
    // ⚠️ منچ has TWO levels, so it must not be labelled from the three-rung «آسان/متوسط/سخت»
    // vocabulary — «متوسط» with no «آسان» anywhere implies a rung that does not exist.
    if (d !== undefined) {
      parts.push(d === "hard" ? fa.mensch.difficultyHard : fa.mensch.difficultyNormal);
    }
  } else if (d === "easy") parts.push(fa.difficulty.easy);
  else if (d === "medium") parts.push(fa.difficulty.medium);
  else if (d === "hard") parts.push(fa.difficulty.hard);
  const players = g.settings?.players;
  if (players === 2) parts.push(fa.mensch.players2);
  else if (players === 3) parts.push(fa.mensch.players3);
  else if (players === 4) parts.push(fa.mensch.players4);
  const side = g.settings?.side;
  if (side === 0) parts.push(fa.chess.colorWhite);
  else if (side === 1) parts.push(fa.chess.colorBlack);
  return parts.join(" · ");
}

function ContinueStrip({
  games,
  onResume,
}: {
  games: readonly ActiveGame[];
  onResume: (game: ActiveGame) => void;
}): ReactNode {
  if (games.length === 0) return null;
  return (
    <section className="flex flex-col gap-sm">
      <h2 className="m-0 px-1 font-headline-sm text-headline-sm text-on-surface">
        {fa.lobby.inProgress}
      </h2>
      {/* Horizontal کشویی row: scroll-snap + hidden scrollbar (Firefox + webkit). */}
      <div
        className="flex snap-x snap-mandatory gap-md overflow-x-auto pb-1 [&::-webkit-scrollbar]:hidden"
        style={{ scrollbarWidth: "none" }}
      >
        {games.map((g, i) => {
          const meta = GAME_META[g.game] ?? { fa: g.game, icon: "🎴" };
          return (
            <button
              key={g.roomId ?? `${g.game}-${i}`}
              type="button"
              onClick={() => onResume(g)}
              aria-label={`${fa.lobby.resume} ${meta.fa}`}
              className="group relative flex h-28 min-w-[164px] shrink-0 snap-start cursor-pointer appearance-none flex-col justify-between overflow-hidden rounded-xl border border-gold-deep/25 bg-gradient-to-br from-felt-deep to-felt-edge p-3 text-right shadow-[0_8px_28px_rgba(0,0,0,0.37)] transition-transform active:scale-95 hover:border-gold-deep/50"
            >
              {/* Soft gold corner glow — themed lobby chrome (design tokens), not artwork. */}
              <span
                aria-hidden="true"
                className="pointer-events-none absolute -right-8 -top-10 h-24 w-24 rounded-full bg-gold-deep/15 blur-2xl"
              />
              {/* «ادامه» badge (start) + the game glyph on a themed disc (end). */}
              <span className="relative z-10 flex items-center justify-between">
                <span className="flex items-center rounded-full bg-gold-deep px-2.5 py-1 font-caption text-caption text-ink-on-gold shadow-sm">
                  {fa.lobby.resume}
                </span>
                <span
                  aria-hidden="true"
                  className="grid h-9 w-9 place-items-center rounded-full border border-gold-deep/30 bg-surface-container/70 text-lg leading-none"
                >
                  <GameMark icon={meta.icon} />
                </span>
              </span>
              {/* Game name (gold) + one-line status. */}
              <span className="relative z-10 block">
                <span className="block truncate font-headline-sm text-headline-sm text-gold-deep">
                  {meta.fa}
                </span>
                {/* ITEM 1 — the settings this save was started with. Several saves of one game
                    can now sit side by side, and «شطرنج» twice over with nothing to tell them
                    apart would be useless; «سخت» / «۳ نفره» is what makes each one findable. */}
                <span className="block truncate font-caption text-caption text-on-surface-variant">
                  {[savedSettingsLabel(g), summaryText(g)].filter(Boolean).join(" · ")}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

type IconName = "menu" | "group" | "store" | "settings" | "help" | "history" | "mail" | "close";

/* ── Inline Material-Symbol SVGs (the app ships no icon font, so each glyph is a
 *    self-contained `currentColor` SVG, mirroring the Stitch `material-symbols`).
 *    `group` is filled (the count chip); the rest are stroked line icons. ── */
function Icon({ name, className }: { name: IconName; className?: string }): ReactNode {
  if (name === "group") {
    return (
      <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
        <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5s-3 1.34-3 3 1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
      </svg>
    );
  }
  const stroke = {
    viewBox: "0 0 24 24",
    className,
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };
  switch (name) {
    case "menu":
      return (
        <svg {...stroke}>
          <path d="M4 7h16M4 12h16M4 17h16" />
        </svg>
      );
    case "store": // shopping_bag
      return (
        <svg {...stroke}>
          <path d="M6.5 8h11l-.8 10.5a1.5 1.5 0 0 1-1.5 1.4H8.8a1.5 1.5 0 0 1-1.5-1.4L6.5 8z" />
          <path d="M9.2 8V6.8a2.8 2.8 0 0 1 5.6 0V8" />
        </svg>
      );
    case "settings": // tune — sliders (reads as "settings")
      return (
        <svg {...stroke}>
          <path d="M4 7h10M18 7h2M4 17h2M10 17h10" />
          <circle cx="16" cy="7" r="2.2" />
          <circle cx="8" cy="17" r="2.2" />
        </svg>
      );
    case "help": // help — "?" in a circle
      return (
        <svg {...stroke}>
          <path d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z" />
          <path d="M9.6 9.3a2.5 2.5 0 0 1 4.8.9c0 1.7-2.4 2-2.4 3.6" />
          <path d="M12 17.3h.01" />
        </svg>
      );
    case "history": // history — clock w/ reverse arrow
      return (
        <svg {...stroke}>
          <path d="M3.5 12a8.5 8.5 0 1 0 2.6-6.1" />
          <path d="M3 4.5V8h3.5" />
          <path d="M12 7.8v4.4l3.2 1.9" />
        </svg>
      );
    case "mail": // mail — envelope (the in-app @bing inbox)
      return (
        <svg {...stroke}>
          <rect x="3" y="5.5" width="18" height="13" rx="2" />
          <path d="M3.5 7l8.5 6 8.5-6" />
        </svg>
      );
    case "close": // close — an X (the drawer's «بستن» control)
      return (
        <svg {...stroke}>
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      );
  }
}

/** Sun / moon glyphs for the theme toggle — inline stroked SVG per app convention. */
function ThemeIcon({ theme, className }: { theme: ResolvedTheme; className?: string }): ReactNode {
  const stroke = {
    viewBox: "0 0 24 24",
    className,
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };
  return theme === "dark" ? (
    // moon — the RESOLVED current theme is dark
    <svg {...stroke}>
      <path d="M20 13.5A8 8 0 0 1 10.5 4a8 8 0 1 0 9.5 9.5z" />
    </svg>
  ) : (
    // sun — the RESOLVED current theme is light
    <svg {...stroke}>
      <circle cx="12" cy="12" r="4.2" />
      <path d="M12 2.5v2.4M12 19.1v2.4M2.5 12h2.4M19.1 12h2.4M5 5l1.7 1.7M17.3 17.3 19 19M19 5l-1.7 1.7M6.7 17.3 5 19" />
    </svg>
  );
}

/**
 * The top-bar dark/light toggle: shows the RESOLVED current theme's glyph
 * (moon = dark, sun = light) and a tap flips to the EXPLICIT opposite mode via
 * `setThemePref` — persisted like every other pref (`bing:theme`), survives a
 * reload, and stamps `<html data-theme>` live. The Settings «پوسته» row keeps
 * the full auto/dark/light choice (خودکار stays reachable there); this button
 * always writes an explicit mode. Stays in sync with that row (or an `auto`
 * Telegram-scheme change) via `onThemeChange`.
 */
function ThemeToggle(): ReactNode {
  const [theme, setTheme] = useState<ResolvedTheme>(getResolvedTheme());
  useEffect(() => onThemeChange(setTheme), []);
  return (
    <button
      type="button"
      aria-label={t.themeToggle}
      data-resolved-theme={theme}
      onClick={() => setThemePref(theme === "dark" ? "light" : "dark")}
      className="flex h-10 w-10 cursor-pointer appearance-none items-center justify-center rounded-full border-0 bg-transparent text-gold-deep transition-transform hover:text-secondary active:scale-95"
    >
      <ThemeIcon theme={theme} className="h-6 w-6" />
    </button>
  );
}

/**
 * The top-bar centre: TOTAL players online right now («N نفر آنلاین», Persian
 * digits, live over the presence socket's `game-counts` pushes). Hidden while
 * 0 — the viewer's own connection counts, so a genuine figure is always ≥ ۱;
 * 0 only means "not connected yet" (never flash a wrong zero).
 */
function OnlineCountChip({ count }: { count: number }): ReactNode {
  if (count <= 0) return <span aria-hidden="true" />;
  return (
    <span
      aria-label={`${faDigits(count)} ${fa.lobby.onlineNow}`}
      className="flex items-center gap-1.5 rounded-full border border-success-win/30 bg-surface-container-high px-3 py-1 shadow-sm"
    >
      <span aria-hidden="true" className="h-1.5 w-1.5 animate-pulse rounded-full bg-success-win" />
      <span
        className="whitespace-nowrap font-caption text-caption text-on-surface"
        style={{ fontVariantNumeric: "tabular-nums" }}
      >
        {faDigits(count)} {fa.lobby.onlineNow}
      </span>
    </span>
  );
}

/**
 * A photographic lobby game tile (the design's "Brand Hero v4" game card): the
 * bundled game photo as a full-bleed background, a dark bottom gradient for text
 * legibility, the top chip row, the game name, and a filled gold CTA.
 *
 * Top chip row (inside the solo tap-target): on the RIGHT (RTL start) the
 * server-driven «بزودی» activation badge when the game is COMING_SOON — shown to
 * admins too, so they always see the public state — next to the LIVE per-game
 * online-players chip (presence `game-counts`; rendered only when > 0, a pulsing
 * dot + Persian digits — an ever-present muted ۰ read as noise on a 5-tile
 * launch grid). On the LEFT, the existing required-players chip, unchanged.
 *
 * `locked` (COMING_SOON for a non-admin) dims the card and disables the tap-target (tap →
 * nothing; the badge already says why) — the server rejects the join anyway, so this is only
 * the cosmetic layer. ADMINS keep the tile tappable, which is how they test an unreleased game.
 *
 * ONE TAP-TARGET (`onOpen`, `aria-label=game.fa`), opening the three-way chooser (تک‌نفره /
 * public / private). The tile used to carry TWO sibling buttons — the photo started a solo game,
 * a gold «آنلاین بازی کن» CTA below it opened the online chooser — which is the only reason the
 * lobby needed a red banner at the top explaining which tap did what. The CTA is gone, the game
 * NAME occupies its slot, and the banner went with it.
 */
function GameCard({
  game,
  comingSoon,
  locked,
  count,
  onOpen,
}: {
  game: GameInfo;
  /** Server activation state — renders the «بزودی» badge (for admins too). */
  comingSoon: boolean;
  /** COMING_SOON for a non-admin: dimmed and NOT launchable. */
  locked: boolean;
  /** Live in-game player count for this game — always shown, ۰ included. `null`: no count known
   *  (a game-server older than «مار»'s count), which shows no count at all rather than a false ۰. */
  count: number | null;
  /** The whole tile is ONE target now: it opens the three-way chooser (solo / public / private). */
  onOpen: () => void;
}): ReactNode {
  const photo = PHOTOS[game.id];
  return (
    <div
      className="relative flex h-44 flex-col overflow-hidden rounded-xl border border-gold-deep/15 p-4 shadow-[0_8px_28px_rgba(0,0,0,0.37)]"
      style={{ opacity: locked ? 0.6 : 1 }}
    >
      {photo ? (
        <ArtPicture
          webp={GAME_ART_WEBP[game.id as GameId]}
          src={photo}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : null}
      {/* THE SCRIM IS LIGHTER ON A PLAYABLE TILE — and that is a FIX, not a preference.
          Removing the gold «آنلاین بازی کن» CTA took ~36px of `bg-gold-deep` off every ACTIVE
          tile (and only active ones — locked tiles never had a CTA). It was the brightest thing
          on the card and the whole reason a playable game read as lit. MEASURED in Chromium with
          the artwork held constant and the «بزودی» tiles as an unchanged control: ACTIVE mean
          luminance fell 52.9 → 33.8 and the active-vs-«بزودی» gap collapsed from 22.6 to 3.4 —
          i.e. playable games stopped looking playable, which is exactly what was reported.
          The photo never changed; the scrim was always this heavy, the button was covering it.
          So lift the scrim where the ARTWORK lives (middle/top) on active tiles, keeping a foot
          dark enough for the name. A locked tile keeps the heavy scrim: dimmed is the point. */}
      <span
        aria-hidden="true"
        className={`pointer-events-none absolute inset-0 bg-gradient-to-t ${
          locked
            ? "from-black/90 via-black/40 to-black/20"
            : "from-black/80 via-black/15 to-transparent"
        }`}
      />

      {/* Tap-target: fills the photo — the chip row sits at its top. */}
      <button
        type="button"
        aria-label={game.fa}
        disabled={locked}
        onClick={locked ? undefined : onOpen}
        className="group relative z-10 flex flex-1 cursor-pointer appearance-none flex-col justify-between border-0 bg-transparent p-0 text-right text-inherit disabled:cursor-default"
      >
        {/* THE CHIP ROW NEVER RUNS PAST ITS TILE (2026-10-20): a chip is ONE line (`whitespace-nowrap`,
            inherited by every chip's text), and when the chips do not fit side by side the ROW wraps
            between them (`flex-wrap`), inside the tile. پاسور's «۲ یا ۴» wrapped to three lines inside its
            chip at 300 px, and even a «۴» row ran 3.5 px (text ×1) to 21 px (×1.5) past the tile's edge,
            into its clip. A row that fits is laid out exactly as before. */}
        <span className="flex flex-wrap items-center justify-between gap-1 whitespace-nowrap">
          {/* RTL start = the RIGHT side: the «بزودی» badge + the live count. Kept
              as a (possibly empty) group so the players chip stays pinned left. */}
          <span className="flex items-center gap-1">
            {comingSoon ? (
              // Gold-on-felt activation badge (design tokens; matches the server gate).
              // The PLAIN «بزودی» badge for everyone — admins see the same public
              // state; the only admin difference is that the tile stays tappable
              // (locked=false for admins), no special label.
              <span className="flex items-center rounded-full border border-gold-deep/40 bg-felt-deep/90 px-2.5 py-1 font-caption text-caption text-gold-deep shadow-sm">
                {fa.lobby.comingSoon}
              </span>
            ) : null}
            {/* Always visible (incl. ۰) on PLAYABLE tiles — live green + pulse when
                players are in, muted when empty. Hidden on coming-soon tiles (a
                count on an unplayable game is noise). ISSUE 5 — the FIGURE now updates on the
                SAME 10s cadence as the top-bar total (the presence room folds per-game counts
                into its 10s flush), so it no longer twitched every couple of seconds. */}
            {comingSoon || count === null ? null : (
              <span
                aria-label={`${faDigits(count)} ${fa.lobby.onlineNow}`}
                className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 shadow-sm ${
                  count > 0 ? "border-success-win/30" : "border-outline-variant/40"
                } bg-black/50`}
                style={{ backdropFilter: "blur(4px)" }}
              >
                <span
                  aria-hidden="true"
                  className={`h-1.5 w-1.5 rounded-full ${
                    count > 0
                      ? "animate-pulse bg-success-win [[data-theme=light]_&]:bg-[#5fd39a]"
                      : "bg-outline-variant [[data-theme=light]_&]:bg-white/50"
                  }`}
                />
                {/* Light overrides: green/muted flip dark on this black chip in light mode. */}
                <span
                  className={`font-caption text-caption ${
                    count > 0
                      ? "text-success-win [[data-theme=light]_&]:text-[#5fd39a]"
                      : "text-on-surface-variant [[data-theme=light]_&]:text-white/80"
                  }`}
                  style={{ fontVariantNumeric: "tabular-nums" }}
                >
                  {faDigits(count)}
                </span>
              </span>
            )}
          </span>
          {/* `game.players` — the table's required player count (static config). `ms-auto` keeps it
              pinned LEFT (the row's end) when the row wraps it onto a line of its own. */}
          <span
            className="ms-auto flex items-center gap-1 rounded-full border border-gold-deep/30 bg-black/50 px-2.5 py-1 shadow-sm"
            style={{ backdropFilter: "blur(4px)" }}
          >
            <Icon
              name="group"
              className="h-3.5 w-3.5 text-success-win [[data-theme=light]_&]:text-[#5fd39a]"
            />
            {/* On the dark chip the flipped `on-surface` (#121c2a) is illegible in light. */}
            <span
              className="font-caption text-caption text-on-surface [[data-theme=light]_&]:text-white"
              style={{ fontVariantNumeric: "tabular-nums" }}
            >
              {game.players}
            </span>
          </span>
        </span>
      </button>

      {/* THE NAME TAKES THE CTA'S PLACE. It used to sit at the BOTTOM OF THE PHOTO, over the dark
          gradient, with a gold «آنلاین بازی کن» button underneath — two tap targets on one tile,
          meaning two different things, which is the only reason the lobby needed a red banner
          explaining itself. The button is gone; the name occupies its slot, and the whole tile is
          one target that opens the three-way chooser.

          ⚠️ THE LIGHT-THEME OVERRIDE + drop-shadow STAY. The card photo is `absolute inset-0`, so
          it covers the WHOLE tile — moving the name out of the photo's bottom strip does NOT move
          it off the artwork (verified in Chromium: the name is painted directly over the `<img>`
          in both themes). In light mode the flipped `gold-deep` is #8b710e, which is illegible on
          a photo; the bright gold is what keeps it readable. Dark is frozen. */}
      <div className="relative z-10 mt-2 text-center">
        <span className="block truncate font-headline-sm text-headline-sm text-gold-deep drop-shadow [[data-theme=light]_&]:text-[#f4cf72]">
          {game.fa}
        </span>
      </div>
    </div>
  );
}

/** A nav-drawer row: a stroked icon + a right-aligned label. */
function DrawerItem({
  icon,
  label,
  onClick,
  className,
  badge = 0,
}: {
  icon: IconName;
  label: string;
  onClick: () => void;
  className?: string;
  /** Unread count — renders a gold chip beside the label when > 0. */
  badge?: number;
}): ReactNode {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex cursor-pointer appearance-none items-center justify-end gap-4 rounded-lg border-0 bg-transparent p-3 text-right text-on-surface-variant transition-colors hover:bg-surface-variant hover:text-on-surface ${
        className ?? ""
      }`}
    >
      {badge > 0 ? (
        <span className="me-auto inline-flex min-w-5 items-center justify-center rounded-full bg-secondary px-1.5 py-0.5 font-caption text-caption text-ink-on-gold">
          {faDigits(badge)}
        </span>
      ) : null}
      <span className="font-body-lg text-body-lg">{label}</span>
      <Icon name={icon} className="h-6 w-6 shrink-0" />
    </button>
  );
}

/** A single online-mode option in the chooser (the app's quick-match / private
 * table). `aria-label={title}` keeps the accessible name exact (the button also
 * holds a tag + hint), so it stays queryable by title. */
/**
 * ONE option in the game chooser. The `tone` is meaning, not decoration — it reuses colour the
 * app has ALREADY taught:
 *   `felt`  — the public room. Green IS "live/other people" here: the lobby's own online-count
 *             dot and every presence indicator are `success-win` green on felt.
 *   `gold`  — your own table. Gold IS "host" here: the crown on a host's seat, the «میزبان»
 *             chips, the host's start button. A private table is the one you own.
 *   `plain` — تک‌نفره. Deliberately UNCOLOURED: it is the option with no other people in it, and
 *             leaving it neutral is what lets the two social options carry the colour meaning.
 */
function ModeButton({
  icon,
  title,
  hint,
  tag,
  tone,
  onClick,
}: {
  icon: string;
  title: string;
  hint: string;
  /** Optional trailing chip — omitted where the title already says everything. */
  tag?: string;
  tone: "felt" | "gold" | "plain";
  onClick: () => void;
}): ReactNode {
  const surface =
    tone === "felt"
      ? "border border-transparent bg-felt-deep text-on-surface shadow-lg"
      : tone === "gold"
        ? "border border-gold-deep/45 bg-gold-deep/15 text-on-surface shadow-lg"
        : "border border-outline-variant bg-surface-container-high text-on-surface";
  const glyph =
    tone === "felt" ? "bg-white/15" : tone === "gold" ? "bg-gold-deep/25" : "bg-surface-container";
  return (
    <button
      type="button"
      aria-label={title}
      onClick={onClick}
      className={`flex w-full cursor-pointer appearance-none items-center gap-3 rounded-lg p-3 text-right transition-transform active:scale-[0.98] ${surface}`}
    >
      <span
        aria-hidden="true"
        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-xl text-gold-deep ${glyph}`}
      >
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-headline-sm text-headline-sm">{title}</span>
          {/* No tag on تک‌نفره: «آفلاین» said nothing the title and hint did not already say,
              and an empty chip is worse than none. */}
          {tag ? (
            <span
              className={`rounded-full px-2 py-0.5 text-[10.5px] font-bold ${
                tone === "felt"
                  ? "bg-white/20 text-on-surface"
                  : tone === "gold"
                    ? "bg-gold-deep/25 text-on-surface"
                    : "bg-surface-container text-on-surface-variant"
              }`}
            >
              {tag}
            </span>
          ) : null}
        </span>
        <span className="mt-0.5 block font-caption text-caption leading-snug text-on-surface-variant">
          {hint}
        </span>
      </span>
      <span
        aria-hidden="true"
        className="shrink-0 text-on-surface-variant"
        style={{ transform: "scaleX(-1)" }}
      >
        <GameIcon type="back" size={18} />
      </span>
    </button>
  );
}

/**
 * Bottom sheet carrying ALL THREE ways to start a game: تک‌نفره, the public room, and a private
 * friend table. Reuses the shared `Sheet` (components/social-ui) — the ONE app-wide bottom-sheet
 * pattern, which also brings its backdrop-fade + panel-slide-up entrance.
 *
 * It used to offer only the two ONLINE modes, because solo lived on a second, separate tap target
 * (the tile photo) while this sheet hid behind a gold «آنلاین بازی کن» CTA. One tile, two meanings,
 * and a red banner at the top of the lobby explaining which was which. Now the whole tile opens
 * this, the three options sit together, and that banner is gone — it was a symptom, not a feature.
 */
/*
 * ⚠️ THE SECOND STEP OF QUICK-MATCH IS GONE (2026-08-26).
 *
 * منچ (seat count), تخته نرد and حکم (match length) each used to ask a question here before
 * quick-matching, because each split its PUBLIC rooms into several room types and a room type IS
 * a matchmaking queue. A public table now offers NO choices at all: it runs the standard rules for
 * its game — حکم seven rounds, منچ four seats, تخته نرد five points — and the whole step, its
 * shell (`QuickMatchStep`), تخته نرد's picker and the `.sheet-step-in` swap animation (keyframes
 * included — a rule nothing applies is dead weight in every client's stylesheet) went with it.
 *
 * The ONE thing a quick-matcher still chooses is WHICH حکم ROOM — the standard game or
 * «اتاق عمومی پیشرفته» — and that is not a question, it is a fourth button on this same list.
 * A private table and a solo game keep every choice they had.
 */
function GameModeChooser({
  game,
  onSolo,
  onQuick,
  onPrivate,
  onClose,
}: {
  game: GameInfo | undefined;
  onSolo: () => void;
  /** `alternate` = the game's SECOND public room was tapped — حکم's «اتاق عمومی پیشرفته» (suit-less
   *  calls), تخته نرد's «اتاق عمومی کوتاه» (three points), پاسور's or مار پله's «اتاق عمومی ۲ نفره» (a
   *  table for two). A DIFFERENT ROOM every time, so the caller records the pick before quick-matching.
   *
   *  ⚠️ ONE flag, THREE games: the caller MUST key it on WHICH game was tapped. Recording it blindly
   *  would let a تخته نرد tap send the player's next حکم quick-match into a room with different
   *  RULES that they never asked for. Every other game only ever passes `false`. */
  onQuick: (alternate: boolean) => void;
  onPrivate: () => void;
  onClose: () => void;
}): ReactNode {
  return (
    <Sheet label={fa.online.chooseMode} onClose={onClose}>
      <div className="mb-3 flex items-center gap-2">
        <button
          type="button"
          aria-label={fa.online.back}
          onClick={onClose}
          className="flex h-9 w-9 cursor-pointer appearance-none items-center justify-center rounded-xl border border-outline-variant bg-surface-container-high text-on-surface"
        >
          <GameIcon type="back" size={18} />
        </button>
        <h3 className="m-0 min-w-0 flex-1 font-headline-sm text-headline-sm text-gold-deep">
          {game ? game.fa : fa.online.chooseMode}
        </h3>
      </div>
      <p className="m-0 mb-3 font-body-md text-body-md text-on-surface-variant">
        {t.chooserPrompt}
      </p>
      <div className="flex flex-col gap-3">
        {/* SOLO FIRST — it is the one that starts instantly, and it is what the tile's photo used
            to do on its own. The two online modes keep their order underneath. */}
        <ModeButton icon="🤖" title={t.soloTitle} hint={t.soloHint} tone="plain" onClick={onSolo} />
        <ModeButton
          icon="⚡"
          title={t.quickTitle}
          hint={t.quickHint}
          /* On تخته نرد this button has a sibling that differs from it ONLY in length, so the tag
             slot has to carry the number — «بدون میزبان» is true of both and would leave the two
             buttons looking identical. Every other game keeps it. */
          tag={
            game?.id === "backgammon"
              ? t.bgStandardTag
              : game?.id === "pasur"
                ? t.pasurFourTag
                : game?.id === "ladders"
                  ? t.laddersFourTag
                  : t.quickTag
          }
          tone="felt"
          onClick={() => onQuick(false)}
        />
        {/*
         * تخته نرد ONLY — «اتاق عمومی کوتاه», the three-point public queue beside the five-point one.
         *
         * THE FIRST EXCEPTION to «a public table offers no choices» (operator, 2026-08-27; پاسور's
         * table for two, below, is the second): the
         * length is a matchmaking QUEUE, not a setting, so it is asked here or nowhere. Somebody
         * who tapped «اتاق عمومی» must never be seated at a three-point table — which is why the
         * two cannot share a room, and why this is a BUTTON rather than the second STEP the lobby
         * used to show (that step is gone platform-wide and is not coming back).
         *
         * ⚠️ IT SITS FLUSH UNDER «اتاق عمومی», WITH NO DIVIDER AND THE SAME FELT GREEN — and that
         * is the OPPOSITE of حکم's second room below, deliberately (operator, 2026-08-28). حکم's
         * is separated because سرس/نرس is a rules VARIANT most players do not know, so it has to
         * read as its own thing. Here BOTH rooms are ordinary تخته نرد and only the LENGTH
         * differs, so they belong together as two of the same kind. Colour still carries meaning
         * in this list (felt = a public room, gold = your own table); two felt buttons is exactly
         * right when there genuinely are two public rooms.
         *
         * ⚠️ THE COST IS REAL AND WAS MEASURED, not assumed: splitting one queue in two roughly
         * halves each side's chance of finding a human inside the 45s fill window. The operator
         * took that trade knowingly; do not "simplify" it back to one room.
         */}
        {game?.id === "backgammon" ? (
          <ModeButton
            icon="⏱"
            title={t.bgShortTitle}
            hint={t.bgShortHint}
            tag={t.bgShortTag}
            tone="felt"
            onClick={() => onQuick(true)}
          />
        ) : null}
        {/*
         * پاسور ONLY — «اتاق عمومی ۲ نفره», the public table for two beside the four-seat one
         * (operator, 2026-10-20): the SECOND exception to «a public table offers no choices», and the
         * same shape as تخته نرد's — a size is a room type, so it is a QUEUE, asked here or nowhere.
         * Flush under «اتاق عمومی», in the same felt green: both rooms are ordinary پاسور and only the
         * SIZE differs, a setting everyone understands (the rule stated with حکم's room below).
         */}
        {game?.id === "pasur" ? (
          <ModeButton
            icon="👥"
            title={t.pasurTwoTitle}
            hint={t.pasurTwoHint}
            tag={t.pasurTwoTag}
            tone="felt"
            onClick={() => onQuick(true)}
          />
        ) : null}
        {/* مار پله ONLY (card-86, the planner's call 22) — «اتاق عمومی ۲ نفره», exactly پاسور's second
            felt button: only the SIZE differs, so it sits flush in the felt group. */}
        {game?.id === "ladders" ? (
          <ModeButton
            icon="👥"
            title={t.laddersTwoTitle}
            hint={t.laddersTwoHint}
            tag={t.laddersTwoTag}
            tone="felt"
            onClick={() => onQuick(true)}
          />
        ) : null}
        <ModeButton
          icon="🔒"
          title={t.privateTitle}
          hint={t.privateHint}
          tag={t.privateTag}
          tone="gold"
          onClick={onPrivate}
        />
        {/*
         * حکم ONLY — «اتاق عمومی پیشرفته», the public room where the Hakem may also call
         * سرس / نرس / آس نرس (and a 7–0 sweep under one of the three pays no کوت).
         *
         * It is a SEPARATE QUEUE, not a setting: somebody who tapped the plain «اتاق عمومی» must
         * never be seated at a table where نرس can be called, so the two cannot share a room.
         * Which is exactly why it is a fourth BUTTON rather than a second step — it is another
         * way to start, like the three above it.
         *
         * ⚠️ SEPARATED BY A DIVIDER AND LEFT `tone="plain"`, deliberately — AND THAT IS THE
         * OPPOSITE OF تخته نرد's SHORT ROOM ABOVE, which sits flush under «اتاق عمومی» in the same
         * felt green. The two are not inconsistent; the difference IS the point (operator,
         * 2026-08-28): سرس/نرس is a rules VARIANT most players do not know, so this one has to
         * read as its own thing rather than as a second, equally-weighted way to quick-match —
         * a felt button here would pull a first-time tap toward the NON-standard rules. تخته نرد's
         * pair are both ordinary backgammon differing only in LENGTH, so they belong together.
         *
         * The rule, stated once: a second public room joins the felt group when it differs only in
         * a SETTING everyone already understands, and is separated when it differs in RULES.
         */}
        {game?.id === "hokm" ? (
          <SecondPublicRoom
            testId="advanced-divider"
            icon="🃏"
            title={t.advancedTitle}
            hint={t.advancedHint}
            onClick={() => onQuick(true)}
          />
        ) : null}
      </div>
    </Sheet>
  );
}

/**
 * A public room that differs from «اتاق عمومی» in its RULES — today حکم's «اتاق عمومی پیشرفته»
 * alone.
 *
 * ⚠️ IT IS NO LONGER SHARED WITH تخته نرد, and that is deliberate rather than drift (operator,
 * 2026-08-28). تخته نرد's «اتاق عمومی کوتاه» is an ordinary backgammon room that differs only in
 * LENGTH, so it sits flush under «اتاق عمومی» in the same felt green, as two of the same kind.
 * This treatment — a divider, and `tone="plain"` like تک‌نفره rather than the felt green — is for a
 * room whose RULES a player may not know: colour carries meaning in this list (felt = a public
 * room, gold = your own table), so a felt button here would read as a second, equally-weighted way
 * to quick-match and pull a first-time tap toward the non-standard option.
 *
 * Use this for a rules variant; put a settings-only sibling in the felt group instead.
 */
function SecondPublicRoom({
  testId,
  icon,
  title,
  hint,
  tag,
  onClick,
}: {
  testId: string;
  icon: string;
  title: string;
  hint: string;
  tag?: string;
  onClick: () => void;
}): ReactNode {
  return (
    <>
      <div aria-hidden data-testid={testId} className="my-1 h-px w-full bg-outline-variant" />
      <ModeButton icon={icon} title={title} hint={hint} tag={tag} tone="plain" onClick={onClick} />
    </>
  );
}

/** Card icon (two overlapping cards) — currentColor so it takes the tab's text colour. */
function CardTabIcon(): ReactNode {
  return (
    <svg width="19" height="19" viewBox="0 0 24 24" aria-hidden fill="currentColor">
      <rect
        x="8"
        y="3.5"
        width="10"
        height="14"
        rx="2"
        opacity="0.4"
        transform="rotate(13 13 10)"
      />
      <rect x="5" y="5.5" width="10" height="14" rx="2" />
    </svg>
  );
}
/** Board icon (a 2×2 checkerboard) — for the board-games tab (chess / backgammon / منچ). */
function BoardTabIcon(): ReactNode {
  return (
    <svg width="19" height="19" viewBox="0 0 24 24" aria-hidden fill="currentColor">
      <rect x="4" y="4" width="7" height="7" rx="1.2" />
      <rect x="13" y="13" width="7" height="7" rx="1.2" />
      <rect x="13" y="4" width="7" height="7" rx="1.2" opacity="0.4" />
      <rect x="4" y="13" width="7" height="7" rx="1.2" opacity="0.4" />
    </svg>
  );
}

/** The two lobby category tabs (card / board) — a segmented pill with an ICON beside each name and
 *  an animated sliding gold indicator. Selecting a tab is instant state; the grid re-mounts and
 *  animates in (no page reload). */
function CategoryTabs({
  category,
  onSelect,
  labels,
}: {
  category: GameCategory;
  onSelect: (c: GameCategory) => void;
  labels: Record<GameCategory, string>;
}): ReactNode {
  return (
    /* A SEGMENTED control, not an underline. The selected tab used to be carried by a 2.5px gold
       rule plus a colour change, which reads as faint at a glance; it is now a solid filled block
       with its own border, in the idiom the app already uses for every other single-select
       (`DifficultySelect`, منچ's player count, تخته‌نرد's match length).
       ⚠️ IT MUST NOT COST HEIGHT — measured on the deployed build before the change: the strip was
       62px at 320×568 (the label wraps beside its icon) and 38px at 440×640. `box-sizing:border-box`
       is global (`styles/theme.css`), and the inactive tab carries a TRANSPARENT border of the same
       width so selecting one never reflows the row.
       ⚠️ **`border-solid` IS LOAD-BEARING, NOT DECORATION.** Tailwind's preflight is OFF in this
       project, so the `border` utility sets a WIDTH and nothing sets `border-style` — the browser
       falls back to `none` and renders no line at all. Measured on the deployed build: this strip
       reported `borderTopWidth: 0px` while carrying `border border-outline-variant`, which is why
       it read as two loose buttons. Every border here therefore names `border-solid`.
       ⚠️ AND A REAL BORDER COSTS HEIGHT, so it is paid for out of the padding it replaces: the
       CONTAINER went `p-1` → `p-[3px]`, trading 1px of padding for its 1px of border. The BUTTONS
       keep `py-1`: measured, their border costs the strip nothing (they are `border-box` inside a
       flex row whose height the container's own padding sets), and taking a pixel from them too
       left the strip 2px SHORT — 64 against the 66 it had to match. The numbers, on the deployed
       build: 66/66/66/42 before, 66/66/66/42 after, and the game grid below it unmoved at 258/234. */
    <div className="flex w-full gap-1 rounded-xl border border-solid border-outline bg-surface-container/70 p-[3px]">
      {GAME_CATEGORY_ORDER.map((cat) => {
        const active = category === cat;
        return (
          <button
            key={cat}
            type="button"
            onClick={() => onSelect(cat)}
            aria-pressed={active}
            className={`flex flex-1 cursor-pointer appearance-none items-center justify-center gap-2 rounded-lg border border-solid px-2 py-1 text-body-lg font-bold transition-colors duration-200 ${
              active
                ? "border-gold-deep bg-secondary text-ink-on-gold shadow-sm"
                : "border-transparent bg-transparent text-on-surface-variant hover:text-on-surface"
            }`}
          >
            {cat === "card" ? <CardTabIcon /> : <BoardTabIcon />}
            <span>{labels[cat]}</span>
          </button>
        );
      })}
    </div>
  );
}

export function Lobby(): ReactNode {
  const setView = useSession((s) => s.setView);
  const setPageReturn = useSession((s) => s.setPageReturn);
  const setActiveGame = useSession((s) => s.setActiveGame);
  const playOnline = useSession((s) => s.playOnline);
  const openGameSettings = useSession((s) => s.openGameSettings);
  const profile = useSession((s) => s.profile);
  const inboxUnread = useSession((s) => s.inboxUnread);
  const activeGames = useSession((s) => s.activeGames);
  const loadActiveGames = useSession((s) => s.loadActiveGames);
  const rejoinOnline = useSession((s) => s.rejoinOnline);
  const resumeOnlineGame = useSession((s) => s.resumeOnlineGame);
  // A live online game to RE-JOIN (this device), read from localStorage — see game/online-resume.
  const [savedOnline, setSavedOnline] = useState<SavedOnlineGame | null>(null);
  // Game activation (server truth; static fallback pre-load) + live counts.
  const lobbyGames = useSession((s) => s.lobbyGames);
  const gameCounts = useSession((s) => s.gameCounts);
  const onlinePlayers = useSession((s) => s.onlinePlayers);
  // Bumped when the saved online game is invalidated out-of-band (a `game_replaced` push) so
  // the strip re-reads localStorage and drops the row live, even on an already-open lobby.
  const resumeStripVersion = useSession((s) => s.resumeStripVersion);
  const loadGames = useSession((s) => s.loadGames);
  // Admins may open + play COMING_SOON games (server-verified: `GET /games`
  // isAdmin = non-suspended Admin row; the game-server GameGate enforces the same
  // bypass on join). The lobby honours it so an admin's tile is actually tappable.
  const isAdmin = useSession((s) => s.isAdmin);

  // Which game's online-mode chooser (quick vs. private) is open, or null.
  const [chooser, setChooser] = useState<string | null>(null);
  // «مار پله»'s chooser opening is the moment to warm its table's picture — one fetch, at most once
  // a page life, never from the lobby's own render or idle (`game/ladders/prefetch.ts`).
  useEffect(() => {
    if (chooser === "ladders") prefetchLaddersScene();
  }, [chooser]);
  // The selected lobby category tab — card games (default) vs board games (chess/backgammon/… منچ).
  // Seeded from (and written back to) localStorage so leaving the lobby and returning keeps the tab.
  const [category, setCategory] = useState<GameCategory>(getLobbyCategory);
  const selectCategory = (next: GameCategory): void => {
    setCategory(next);
    setLobbyCategory(next);
  };
  // Slide-out nav drawer (design uses a raw-DOM script; we drive it with state).
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Refresh the "continue playing" strip whenever the lobby is (re)opened — the solo games from the
  // server AND the (client-side) live online game to re-join.
  useEffect(() => {
    void loadActiveGames();
    setSavedOnline(loadOnlineGame());
  }, [loadActiveGames, resumeStripVersion]);

  // Refresh the activation list on every lobby (re)open — cheap (the server
  // caches ~5s) and picks up a live admin toggle without an app restart. The
  // live per-game counts need no polling: they ride the presence socket.
  useEffect(() => {
    void loadGames();
  }, [loadGames]);

  /**
   * STARTING A GAME. The game tiles are raw `<button>`s (not the shared `Button`, which
   * clicks), so tapping one used to be SILENT — the first sound was the shuffle, a
   * couple of seconds later, once the table had loaded. Give the tap its own card cue:
   * a single dealt card. The hand fanning in still gets the shuffle, so the sequence
   * reads «flick → shuffle» instead of «nothing → shuffle».
   */
  const startCue = (id?: string): void => {
    // The cue has to belong to the GAME. A card-deal flick opening منچ or تخته نرد was simply
    // the wrong instrument — those have no cards in them. Board games get the low "a game
    // begins" gong instead, and chess now gets one too: it used to be skipped entirely on the
    // theory that its own gong would cover it, but chess opens on a difficulty SCREEN first,
    // so the tap was answered with silence.
    const category = id ? GAME_CATEGORY[id as GameId] : undefined;
    playSfx(category === "board" ? "boardStart" : "deal");
  };

  const startSolo = (id: string): void => {
    startCue(id);
    setActiveGame(id);
    setView("game");
  };

  /**
   * ITEM 1 — a game TILE always starts a NEW offline game.
   *
   * It no longer asks anything and it never touches what is already saved: the new game is
   * ADDED to «ادامه بازی» beside the old one, each with the settings it was started with, so a
   * player can hold chess on medium and chess on hard at once. Previously the saved game won
   * by default, so tapping a tile with new settings silently reopened the old game — and the
   * fix after that made «بازی جدید» REPLACE it, which threw the old game away instead.
   *
   * The only thing that can now refuse a new game is the concurrent-games cap, which the
   * server enforces across every game, board and card alike (ITEM 2).
   */
  const play = (id: string): void => {
    startSolo(id);
  };

  /** «مار»: straight to its own start screen, with the board games' cue. */
  const openSnake = (): void => {
    playSfx("boardStart");
    setActiveGame(SNAKE_TILE.id);
    setView("game");
  };

  /** «سودوکو»: straight to its own start screen, with the board games' cue — as «مار». */
  const openSudoku = (): void => {
    playSfx("boardStart");
    setActiveGame(SUDOKU_TILE.id);
    setView("game");
  };

  // Resuming a persisted game. `setActiveGame` forces `online:false` (the solo room), which is
  // correct TODAY because persistence is solo-only — every `ActiveGame` is a solo game. GUARD
  // (latent trap): if online games ever persist as `ActiveGame`, resuming one needs its roomId +
  // a reconnection token, NOT just the game id — routing it through the solo `play()` would
  // silently drop the player into a SOLO room. So branch on the row's `mode` and refuse to
  // misroute a non-solo game until online resume is actually wired.
  const resume = (g: ActiveGame): void => {
    // DB-backed ONLINE CHESS resume (cross-device): join `chess-online` by the stable onlineGameId
    // (filterBy), which rehydrates the persisted game for BOTH players. Takes precedence over the
    // same-device localStorage-token rejoin below (that one has no onlineGameId).
    if (g.onlineGameId) {
      startCue(g.game);
      resumeOnlineGame(g.game, g.onlineGameId);
      return;
    }
    if (g.mode === "online") {
      // RE-JOIN a live online game: reconnect with the saved token and reclaim our seat from the
      // bot. If the token is stale (the match ended / a newer game replaced it), drop the entry.
      const saved = loadOnlineGame();
      if (saved && saved.roomId === g.roomId) {
        startCue(saved.game);
        rejoinOnline(saved.game, saved.roomId, saved.token);
      } else {
        clearOnlineGame(g.roomId);
        setSavedOnline(null);
      }
      return;
    }
    if (g.mode !== "solo") {
      console.warn(`[resume] "${g.game}" is mode=${g.mode}; resume not wired — skipped`);
      return;
    }
    // ITEM 1 — resume THE SAVE THAT WAS TAPPED, by its row id. Several saves of one game can
    // sit in this strip, so naming only the game would reopen an arbitrary one of them. The
    // same signal tells a board game's start screen to stand aside instead of collecting
    // settings the save is about to override.
    if (g.id) setResumeSolo(g.id);
    startSolo(g.game);
  };

  // The strip shows the (client-side) live online game to re-join FIRST, then the server's solo
  // games. The online entry is synthetic — `resume` reads its token from localStorage.
  const onlineEntry: ActiveGame | null = savedOnline
    ? {
        game: savedOnline.game,
        mode: "online",
        roomId: savedOnline.roomId,
        // A PRE-START private table rides this strip too (the host who closed the app in their own
        // waiting room). The heading above says «بازی‌های در جریان», so the card carries its own
        // line saying no game has started — `summaryText` prefers a server-sent string over the
        // mode label, which is exactly the seam this needs. A dealt table sets nothing and keeps
        // reading «آنلاین», unchanged.
        ...(savedOnline.pending ? { summary: fa.lobby.tableNotStarted } : {}),
        updatedAt: new Date(savedOnline.savedAt).toISOString(),
      }
    : null;
  // ONE GAME, ONE CARD. A resumable online game (chess) is listed TWICE otherwise: once from
  // this device's re-join token and once from the server's persisted `OnlineGame` row. They
  // are the same game — the room announces its row id, so the two can be matched exactly —
  // and the SAME-DEVICE entry wins: its token reclaims the seat in the room that is still
  // running, whereas the DB entry rehydrates the position into a fresh room. If that token
  // ever goes stale the re-join clears it, and the DB row is offered again on the next open.
  const serverGames =
    savedOnline?.onlineGameId === undefined
      ? activeGames
      : activeGames.filter((g) => g.onlineGameId !== savedOnline.onlineGameId);
  // A game hidden from players (`HIDDEN_GAMES` — بلوف, operator 2026-10-03) is never offered back,
  // from either door: an old ticket on this device or a server-side save.
  const continueGames = (onlineEntry ? [onlineEntry, ...serverGames] : serverGames).filter(
    (g) => !isHiddenGame(g.game),
  );

  const profileName = profileDisplayName(profile);
  const profileAvatarId = profile?.avatarId ?? "fox";

  // Each drawer item opens a real lobby-level destination (its own View): the
  // Store, Settings, Guides (Help), and History screens. `بستن` just closes the
  // drawer (a Telegram Mini App has no "log out" — the user is always authed via
  // initData). `کاربر میهمان` + subtitle are static placeholders (the design's
  // level/points have no backend and are intentionally omitted, not faked).
  const goto = (view: View): void => {
    setDrawerOpen(false);
    setPageReturn("lobby"); // a drawer-opened Settings/History backs to the lobby
    setView(view);
  };

  return (
    <div
      className="relative flex min-h-full flex-col bg-background text-on-surface"
      style={{
        backgroundImage:
          "radial-gradient(circle at top, rgb(var(--c-room-light) / 0.8) 0%, rgb(var(--c-surface)) 80%)",
      }}
      dir="rtl"
    >
      {/* TopAppBar — the signed-in user's profile (avatar + name → Profile) in the
          leading column (unchanged), the LIVE online-player counter EXACTLY
          CENTERED, and the theme toggle beside the nav-drawer menu in the
          trailing column (a 1fr/auto/1fr grid, so a long profile name truncates
          in its own column and can never crowd the centre). Sticky (NOT fixed)
          so it pins inside App's scroll container. */}
      <header
        className="sticky top-0 z-30 border-b border-outline-variant/40 bg-surface/90 shadow-sm"
        style={{ backdropFilter: "blur(12px)" }}
      >
        <div className="mx-auto grid h-16 max-w-container-max grid-cols-[1fr_auto_1fr] items-center px-md">
          <button
            type="button"
            aria-label={fa.lobby.profileAria}
            onClick={() => setView("profile")}
            className="flex min-w-0 items-center gap-2 justify-self-start cursor-pointer appearance-none rounded-full border-0 bg-transparent p-1 pl-3 transition-transform active:scale-95 hover:bg-surface-variant/40"
            style={{ maxWidth: "100%" }}
          >
            <Avatar
              avatarId={profileAvatarId}
              avatarPhotoId={profile?.avatarPhotoId}
              size={36}
              ringClass="border-gold-deep/50 shadow-sm"
            />
            {/* `dir="auto"`: an RTL block clips the BEGINNING of a Latin name (`## Gotchas`). */}
            <span
              dir="auto"
              className="min-w-0 max-w-[38vw] truncate font-headline-sm text-headline-sm text-on-surface"
            >
              {profileName}
            </span>
          </button>

          {/* Centre — the live «N نفر آنلاین» counter (presence game-counts pushes). */}
          <OnlineCountChip count={onlinePlayers} />

          {/* Trailing cluster — theme toggle NEXT TO the drawer menu (menu stays
              at the outer edge). */}
          <div className="flex items-center gap-0.5 justify-self-end">
            <ThemeToggle />
            <button
              type="button"
              aria-label={inboxUnread > 0 ? `${t.menu} (${faDigits(inboxUnread)})` : t.menu}
              onClick={() => setDrawerOpen(true)}
              className="relative flex h-10 w-10 cursor-pointer appearance-none items-center justify-center rounded-full border-0 bg-transparent text-gold-deep transition-transform hover:text-secondary active:scale-95"
            >
              <Icon name="menu" className="h-6 w-6" />
              {inboxUnread > 0 ? (
                <span className="absolute -right-0.5 -top-0.5 inline-flex min-w-[17px] items-center justify-center rounded-full border border-background bg-secondary px-1 py-px text-[10px] font-bold leading-none text-ink-on-gold shadow-sm">
                  {inboxUnread > 9 ? "۹+" : faDigits(inboxUnread)}
                </span>
              ) : null}
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-container-max flex-col gap-md px-md py-md pb-8">
        {/* Brand hero banner — deliberately compact (a slim brand strip, not a big
            promo), so the game grid + resume strip sit higher on the screen. */}
        <section className="relative flex items-center justify-between overflow-hidden rounded-xl border border-gold-deep/30 bg-gradient-to-r from-felt-deep to-background p-md shadow-lg">
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_left,_var(--tw-gradient-stops))] from-primary-container/10 to-transparent"
          />
          <div className="relative z-10 h-12 w-12 shrink-0">
            <img
              src={logoUrl}
              alt=""
              className="h-full w-full rounded-xl object-contain"
              style={{ filter: "drop-shadow(0 0 8px rgba(199,154,63,0.5))" }}
            />
          </div>
          <div className="relative z-10 text-right">
            <h2 className="m-0 font-headline-md text-headline-md text-gold-deep">{t.brand}</h2>
            <p className="m-0 font-caption text-caption text-on-surface-variant">{t.tagline}</p>
          </div>
        </section>

        {/* Category tabs — «بازی‌های کارتی» / «بازی‌های تخته‌ای», right below the header + notice.
            Selecting one filters the grid below; the switch is instant + animated (no reload). */}
        <CategoryTabs
          category={category}
          onSelect={selectCategory}
          labels={{ card: t.cardGames, board: t.boardGames }}
        />

        {/* Managed promo slots — the lobby hero banner + a secondary strip under it.
            Both render NOTHING today (the `promos` flag is off / no campaign); a
            null slot produces no DOM node, so the flex gap is unaffected. */}
        <PromoSlot slot="LOBBY_HERO" />
        <PromoSlot slot="SECONDARY_STRIP" />

        {/* "Continue playing" strip — the user's in-progress games (real: reads
            `GET /me/active-games`). Renders nothing when there are none. */}
        <ContinueStrip games={continueGames} onResume={resume} />

        {/* 2-column photographic game grid, strictly in the server's canonical
            order (hokm, hearts, shelem, pasur, bluff — `LOBBY_GAME_ORDER`; the
            store falls back to it over the static catalogue while `GET /games`
            is in flight). A COMING_SOON game is badged AND locked for everyone —
            admins included; the server enforces the same gate on join. (The
            opponent-difficulty preference lives on the Settings screen — the
            single source of truth — and is read by the game connectors when a
            game starts.) */}
        <div key={category} className="game-grid-in grid grid-cols-2 gap-md">
          {lobbyGames
            .filter((entry) => GAME_CATEGORY[entry.game] === category)
            .map((entry) => {
              const game = GAMES.find((g) => g.id === entry.game);
              if (!game) return null; // a game the catalogue doesn't know yet
              const comingSoon = entry.status === "COMING_SOON";
              // Coming-soon locks the tile for regular users; ADMINS bypass and can
              // launch it (product decision 2026-07-11 — supersedes the 07-10 "locked
              // for everyone" note; the server GameGate already allows admins). The
              // badge still shows so admins see the public state (see GameCard).
              const locked = comingSoon && !isAdmin;
              return (
                <GameCard
                  key={entry.game}
                  game={game}
                  comingSoon={comingSoon}
                  locked={locked}
                  count={gameCounts[entry.game]}
                  onOpen={() => setChooser(entry.game)}
                />
              );
            })}
          {category === "board" ? (
            <GameCard
              key={SNAKE_TILE.id}
              game={SNAKE_TILE}
              comingSoon={false}
              locked={false}
              count={gameCounts.snake ?? null}
              onOpen={openSnake}
            />
          ) : null}
          {category === "board" ? (
            <GameCard
              key={SUDOKU_TILE.id}
              game={SUDOKU_TILE}
              comingSoon={false}
              locked={false}
              count={gameCounts.sudoku ?? null}
              onOpen={openSudoku}
            />
          ) : null}
        </div>
      </main>

      {/* Slide-out nav drawer (right side), driven by React state (not raw DOM).
          Kept mounted for the slide transition; hidden from AT + non-interactive
          when closed. As a `fixed` overlay it clears the shared bottom nav. */}
      <div
        className={`fixed inset-0 z-[60] ${drawerOpen ? "" : "pointer-events-none"}`}
        aria-hidden={!drawerOpen}
      >
        <div
          onClick={() => setDrawerOpen(false)}
          className={`absolute inset-0 bg-black/50 transition-opacity duration-300 ${
            drawerOpen ? "opacity-100" : "opacity-0"
          }`}
          style={{ backdropFilter: drawerOpen ? "blur(2px)" : "none" }}
        />
        <nav
          aria-label={t.menu}
          // As a `fixed`-overlay child the drawer escapes the App shell's safe-area padding, so
          // it applies the SAME insets the lobby uses (top: status bar / Dynamic Island / Telegram
          // header; bottom: home indicator / Telegram footer) — content never sits under Telegram's
          // top or bottom chrome. env() covers windowed iOS; --tg-safe-* covers fullscreen (iOS
          // zeroes env there). Base 16px keeps the previous `py-md` breathing room.
          style={{
            paddingTop: "calc(16px + max(env(safe-area-inset-top), var(--tg-safe-top, 0px)))",
            paddingBottom:
              "calc(16px + max(env(safe-area-inset-bottom), var(--tg-safe-bottom, 0px)))",
          }}
          className={`absolute right-0 top-0 flex h-full w-80 max-w-[85%] flex-col overflow-y-auto border-l border-outline-variant bg-surface-container-lowest px-sm text-right shadow-xl will-change-transform transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${
            drawerOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="mb-4 flex items-center justify-end gap-md border-b border-surface-variant p-md">
            <div className="min-w-0 flex-1 text-right">
              {/* The signed-in player's name — SAME reactive source + priority (nickname → Telegram
                  name) as the top bar (`profileName`), so a nickname set/change/clear or a Telegram
                  name change updates here live. Truncates on one line so a long name never overflows. */}
              {/* `dir="auto"`: an RTL block clips the BEGINNING of a Latin name (`## Gotchas`). */}
              <h2
                dir="auto"
                className="m-0 truncate font-headline-md text-headline-md text-gold-deep"
              >
                {profileName}
              </h2>
              {/* The design showed «سطح ۱۲ | امتیاز: ۲,۵۰۰» here; there is no
                  leveling/points backend, so we show a neutral welcome line rather
                  than fabricating stats. */}
              <p className="m-0 font-body-md text-body-md text-on-surface-variant">
                {t.guestSubtitle}
              </p>
            </div>
            <div className="h-12 w-12 shrink-0 overflow-hidden rounded-full border-2 border-gold-deep/50">
              <img src={logoUrl} alt="" className="h-full w-full object-cover" />
            </div>
          </div>

          <div className="flex flex-1 flex-col gap-2">
            {/* Each item opens its own real lobby-level screen (its own View). */}
            <DrawerItem
              icon="mail"
              label={t.drawerMessages}
              onClick={() => goto("messages")}
              badge={inboxUnread}
            />
            <DrawerItem icon="store" label={t.drawerStore} onClick={() => goto("store")} />
            <DrawerItem icon="settings" label={t.drawerSettings} onClick={() => goto("settings")} />
            <DrawerItem icon="help" label={t.drawerHelp} onClick={() => goto("help")} />
            <DrawerItem icon="history" label={t.drawerHistory} onClick={() => goto("history")} />
            <DrawerItem
              icon="close"
              label={t.drawerClose}
              onClick={() => setDrawerOpen(false)}
              className="mt-auto border-t border-surface-variant pt-4"
            />
          </div>
        </nav>
      </div>

      {chooser ? (
        <GameModeChooser
          game={GAMES.find((g) => g.id === chooser)}
          onSolo={() => {
            setChooser(null);
            play(chooser);
          }}
          onQuick={(alternate) => {
            setChooser(null);
            /*
             * WHICH ROOM — remembered here and read by the game's own container when it builds the
             * room name. Recorded on BOTH taps of the game that was tapped, so a player who tries
             * the second room once and then goes back to the plain one is never left in the wrong
             * queue.
             *
             * ⚠️ KEYED ON THE GAME, AND THAT IS THE WHOLE POINT. `onQuick` is ONE callback shared
             * by every game, and حکم used to be its only second-room caller. Writing both couriers
             * blindly would make a تخته نرد «کوتاه» tap ALSO flip the حکم flag — and the player's
             * next حکم quick-match would silently land in «اتاق عمومی پیشرفته», a room with
             * different RULES, that they never chose. Each game writes its own courier, only.
             */
            if (chooser === "hokm") {
              setHokmPublicAdvanced(alternate);
            } else if (chooser === "backgammon") {
              setBackgammonPublicMatchTo(
                alternate ? BACKGAMMON_SHORT_MATCH_LENGTH : DEFAULT_BACKGAMMON_MATCH_LENGTH,
              );
            } else if (chooser === "pasur") {
              setPasurPublicSeats(alternate ? 2 : 4);
            } else if (chooser === "ladders") {
              setLaddersPublicSeats(alternate ? 2 : 4);
            }
            startCue(chooser ?? undefined); // same cue as a solo start (chess → its own gong)
            playOnline(chooser);
          }}
          onPrivate={() => {
            setChooser(null);
            openGameSettings(chooser); // the CUE lands when the host actually starts the
            // table (WaitingRoom's start button) — this only opens the settings screen.
          }}
          onClose={() => setChooser(null)}
        />
      ) : null}
    </div>
  );
}
